import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.tsx";
import { OtpInput } from "../../components/OtpInput.tsx";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { approveEnrollment, approveMessage, fetchPendingEnrollment, type PendingEnrollment } from "./approve-client.ts";
import "./approve.css";

/**
 * The enrollment approval page (C2). The AIS `/activate` page redirects an approver-bound enrollment
 * here with `?user_code=…`. The page shows what the agent asked for and who must approve it. Only the
 * recorded approver (the license holder) gets the two-factor prompt; anyone else is told who can
 * approve. The server enforces both regardless: the BFF checks the code, the AIS checks the approver.
 */

const TITLE = "Approve agent";
const SUBTITLE = "An agent is asking to enroll under your license. Check the details before you approve it.";
const SUCCESS = "Agent approved. The agent is collecting its token now; you can close this tab.";

type Load =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly code: string }
  | { readonly kind: "ready"; readonly pending: PendingEnrollment };

export function ApprovePage(): ReactNode {
  const [params] = useSearchParams();
  const userCode = (params.get("user_code") ?? "").trim();
  const viewerId = useAuth().identity?.user.id ?? null;
  const [load, setLoad] = useState<Load>(userCode.length === 0 ? { kind: "failed", code: "not_found" } : { kind: "loading" });

  useEffect(() => {
    if (userCode.length === 0) {
      setLoad({ kind: "failed", code: "not_found" });
      return;
    }
    const controller = new AbortController();
    setLoad({ kind: "loading" });
    void fetchPendingEnrollment(userCode, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setLoad(result.ok ? { kind: "ready", pending: result.body } : { kind: "failed", code: result.code });
    });
    return () => controller.abort();
  }, [userCode]);

  const state: ScreenState =
    load.kind === "loading"
      ? { kind: "loading" }
      : load.kind === "failed"
        ? { kind: "error", message: approveMessage(load.code) }
        : { kind: "ready" };

  return (
    <ScreenScaffold title={TITLE} subtitle={SUBTITLE} state={state}>
      {load.kind === "ready" && (
        <div className="approve">
          <Details pending={load.pending} />
          {viewerId !== load.pending.approver.sub ? (
            <p className="approve__note" role="note">
              Only {load.pending.approver.display} can approve this agent.
            </p>
          ) : (
            <ApproveForm userCode={userCode} anchorKey={load.pending.key} />
          )}
        </div>
      )}
    </ScreenScaffold>
  );
}

function shown(value: string | null | undefined): string {
  return typeof value === "string" && value.length > 0 ? value : "Not given";
}

function Details(props: { pending: PendingEnrollment }): ReactNode {
  const { pending } = props;
  return (
    <dl className="approve__details">
      <dt>Agent</dt>
      <dd>{shown(pending.label)}</dd>
      <dt>Platform</dt>
      <dd>{shown(pending.platform)}</dd>
      <dt>Key</dt>
      <dd>
        <code className="approve__key">{pending.key}</code>
      </dd>
      <dt>Requested clearance</dt>
      <dd>{shown(pending.requested_clearance)}</dd>
      <dt>Approver</dt>
      <dd>{pending.approver.display}</dd>
    </dl>
  );
}

function ApproveForm(props: { userCode: string; anchorKey: string }): ReactNode {
  const codeId = useId();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const result = await approveEnrollment(props.userCode, props.anchorKey, code);
      if (result.ok) {
        setDone(true);
        return;
      }
      if (result.code === "mfa_required") setCode("");
      setError(approveMessage(result.code));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="approve__done" role="status">
        {SUCCESS}
      </p>
    );
  }
  return (
    <form className="approve__form" onSubmit={(e) => void submit(e)} noValidate>
      <div className="approve__field">
        <label htmlFor={codeId}>Two-factor code</label>
        <OtpInput id={codeId} value={code} onChange={setCode} disabled={busy} required align="start" />
      </div>
      {error !== null && (
        <p className="approve__err" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="approve__btn" disabled={busy || code.length !== 6}>
        {busy ? "Approving…" : "Approve"}
      </button>
    </form>
  );
}
